import { useTheme } from '@emotion/react';
import { Typography, Box } from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { type GridRowData } from 'components/customDataGrid/CustomDataGrid';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { strings } from 'i18n/Strings';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getDailyFundPrice } from 'service/FundManagement';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { colorTheme } from 'theme';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import Tooltip from '@mui/material/Tooltip';

interface IDailyFundPrice {
  id: number;
  fundName: string;
  date: string;
  type: string;
  symbol: string;
  amount: string;
}

const DailyFundPrice = (): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [fundPriceDetails, setFundPriceDetails] = useState<IDailyFundPrice[]>(
    [],
  );
  const pageInfo = useSelector((state) => state?.pagination?.dailyFundPrice);
  const [pageCount, setPageCount] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };
  useEffect(() => {
    void getAllDailyFundPrice();
  }, [
    pageInfo?.page,
    pageInfo.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    pageInfo.pageSize,
    searchValue,
  ]);

  const getAllDailyFundPrice = async (): Promise<void> => {
    try {
      const data = await getDailyFundPrice(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        searchValue,
      );
      setFundPriceDetails(data?.rows);
      setPageCount(data?.count);
    } catch (error) {
      console.log(error);
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 0.75,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.publishedDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={13}>
          {dateFormat(row?.valuationDate)}
        </Typography>
      ),
    },
    {
      field: 'fundId',
      headerName: 'Fund ID',
      flex: 0.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={13}>
          {row?.fundId}
        </Typography>
      ),
    },
    {
      field: 'fund.description',
      headerName: 'Fund Name',
      flex: 2.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row?.fund?.description} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={13}>
            {row?.fund?.shortDescription}
          </Typography>
        </Tooltip>
      ),
    },

    {
      field: 'fund.fund_type',
      headerName: 'Fund Type',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundType}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={13}>
          {row?.fund?.fund_type}
        </Typography>
      ),
    },
    {
      field: 'fund.symbol',
      headerName: 'Symbol',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.symbol}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={13}>
          {row?.fund?.symbol}
        </Typography>
      ),
    },
    {
      field: 'todayPrice',
      headerName: 'Amount',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.amountEuro}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={13}>
          {CurrencyFormater(Number(row?.todayPrice).toFixed(2))}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <CustomDataGrid
        title={strings.displayText.dailyFundPriceTitle}
        getRowId={(row: GridRowData) => row.id}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        columns={columns}
        rows={fundPriceDetails}
        pageCount={pageCount}
        currentPage="dailyFundPrice"
        searchEnabled={true}
        handleSearch={handleSearch}
        searchPlaceholder={strings.displayText.searchLabel10}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode: unknown[][]) => {
          dispatch(
            setSortBy({
              key: 'dailyFundPrice',
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'dailyFundPrice',
              value: mode[0].sort,
            }),
          );
          dispatch(
            setCurrentPage({
              key: 'dailyFundPrice',
              value: 0,
            }),
          );
        }}
      />
    </Box>
  );
};

export default DailyFundPrice;
